import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { BsChevronLeft, BsList, BsBuilding, BsChevronDown } from "react-icons/bs";
import { AiOutlineAppstore } from "react-icons/ai";
import { FaUsers, FaLeaf, FaBell, FaShoppingCart } from "react-icons/fa";
import { MdInventory2 } from "react-icons/md";
import { clearSession, getFPOData } from "../../helpers/local_storage_helpers";
import { CHANNEL_OPTIONS } from "../../pages/notifications/shared";
import ProfileDropdown from "../header/ProfileDropdown";
import logo from "../../assets/icons/Rashail-agro-logo.png";

const Sidebar = () => {
    const location = useLocation();
    const navigate  = useNavigate();
    const [searchParams] = useSearchParams();
    const isTab    = useMediaQuery({ query: "(max-width: 768px)" });

    const [isOpen, setIsOpen] = useState(() => {
        const saved = localStorage?.getItem("sidebar_is_open");
        return saved !== null ? JSON.parse(saved) : !isTab;
    });

    const [communicationOpen, setCommunicationOpen] = useState(
        location.pathname.startsWith("/notifications")
    );

    useEffect(() => {
        if (location.pathname.startsWith("/notifications")) setCommunicationOpen(true);
    }, [location.pathname]);

    useEffect(() => {
        localStorage?.setItem("sidebar_is_open", JSON.stringify(isOpen));
    }, [isOpen]);

    useEffect(() => {
        setIsOpen(!isTab);
    }, [isTab]);

    const handleLogout = () => {
        clearSession();
        navigate("/");
    };

    // ── Nav items ────────────────────────────────────────────────
    const navItems = [
        {
            path:  "/dashboard",
            icon:  <AiOutlineAppstore className="text-lg" />,
            label: "Dashboard",
            key:   "dashboard",
        },
        {
            path:  "/profile",
            icon:  <BsBuilding className="text-lg" />,
            label: "FPO Profile",
            key:   "profile",
        },
        {
            path:  "/staff",
            icon:  <FaUsers className="text-lg" />,
            label: "Staff",
            key:   "staff",
        },
        {
            path:  "/commodities",
            icon:  <FaLeaf className="text-lg" />,
            label: "Commodities",
            key:   "commodities",
        },
        {
            path:  "/members",
            icon:  <FaUsers className="text-lg" />,
            label: "Members",
            key:   "members",
        },
        {
            path:  "/procurement",
            icon:  <FaShoppingCart className="text-lg" />,
            label: "Procurement",
            key:   "procurement",
        },
        {
            path:  "/assets",
            icon:  <MdInventory2 className="text-lg" />,
            label: "Asset Register",
            key:   "assets",
        },
        {
            path:  "/notifications",
            icon:  <FaBell className="text-lg" />,
            label: "Communication",
            key:   "notifications",
            children: CHANNEL_OPTIONS,
        },
        // ── Future screens (placeholders) ─────────────────────
        // {
        //   path: "/transactions", icon: <FaMoneyBillWave className="text-lg" />, label: "Transactions", key: "transactions",
        // },
        // {
        //   path: "/invoices", icon: <FaFileInvoice className="text-lg" />, label: "Invoices", key: "invoices",
        // },
        // {
        //   path: "/warehouse", icon: <FaWarehouse className="text-lg" />, label: "Warehouse", key: "warehouse",
        // },
        // {
        //   path: "/support", icon: <MdSupportAgent className="text-lg" />, label: "Support", key: "support",
        // },
        // {
        //   path: "/settings", icon: <MdOutlineSettings className="text-lg" />, label: "Settings", key: "settings",
        // },
    ];

    const isActive = (path) => {
        if (path === "/dashboard") return location.pathname === path;
        return location.pathname.startsWith(path);
    };

    return (
        <>
            {/* Mobile overlay */}
            <AnimatePresence>
                {isTab && isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsOpen(false)}
                        className="fixed inset-0 z-[998] bg-black/50 backdrop-blur-sm"
                    />
                )}
            </AnimatePresence>

            {/* Desktop toggle button */}
            <div
                className={`fixed top-4 z-[1000] transition-all duration-300 hidden md:block ${
                    isOpen ? "left-[15rem] -ml-3.5" : "left-4"
                }`}
            >
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="bg-white border-2 border-primary shadow-md w-7 h-7 rounded-full text-primary hover:bg-primary hover:text-white transition-all duration-200 flex items-center justify-center transform hover:scale-110 active:scale-95 cursor-pointer"
                    title={isOpen ? "Close Sidebar" : "Open Sidebar"}
                >
                    {isOpen ? <BsChevronLeft size={14} /> : <BsList size={16} />}
                </button>
            </div>

            {/* Mobile toggle button */}
            <div className="md:hidden fixed top-3 left-4 z-[1000]">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="bg-white border border-gray-200 shadow-lg p-2 rounded-lg text-primary"
                >
                    <BsList size={24} />
                </button>
            </div>

            {/* Sidebar panel */}
            <motion.div
                initial={false}
                animate={{
                    x:     isOpen ? 0 : isTab ? -300 : -240,
                    width: isOpen ? "15rem" : "0rem",
                }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className={`bg-[#0f2b21] text-white/70 shadow-xl h-full flex flex-col md:relative fixed z-[999] ${
                    isOpen ? "" : "overflow-hidden"
                }`}
            >
                {/* Brand */}
                <div
                    className={`flex items-center gap-2.5 px-4 py-[1.15rem] border-b border-white/10 transition-opacity duration-200 ${
                        isOpen ? "opacity-100" : "opacity-0"
                    }`}
                >
                    <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <img src={logo} alt="Rashail Agro" className="w-full h-full object-contain p-1" />
                    </div>
                    <div className="min-w-0 leading-tight">
                        <p className="text-white text-sm font-bold truncate">{getFPOData()?.name || "Rashail Agro"}</p>
                        <p className="text-white/40 text-[11px] truncate">FPO Console</p>
                    </div>
                </div>

                {/* Nav items */}
                <div
                    className={`flex flex-col flex-1 min-h-0 transition-opacity duration-200 ${
                        isOpen ? "opacity-100" : "opacity-0"
                    }`}
                >
                    <p className="px-5 pt-4 pb-2 text-[10px] font-bold text-white/35 tracking-widest uppercase">
                        Operations
                    </p>
                    <ul className="whitespace-pre px-2.5 text-[0.9rem] flex flex-col gap-1 font-medium">
                        {navItems.map((item) => {
                            if (!item.children) {
                                return (
                                    <li key={item.key}>
                                        <NavLink
                                            to={item.path}
                                            onClick={() => isTab && setIsOpen(false)}
                                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                                                isActive(item.path)
                                                    ? "bg-primary text-white shadow-md"
                                                    : "hover:bg-white/10 hover:text-white text-white/60"
                                            }`}
                                        >
                                            {item.icon}
                                            <span className="font-medium">{item.label}</span>
                                        </NavLink>
                                    </li>
                                );
                            }

                            const activeChannel = searchParams.get("channel") || "app_push";
                            return (
                                <li key={item.key}>
                                    <button
                                        type="button"
                                        onClick={() => setCommunicationOpen((o) => !o)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                                            isActive(item.path)
                                                ? "bg-primary text-white shadow-md"
                                                : "hover:bg-white/10 hover:text-white text-white/60"
                                        }`}
                                    >
                                        {item.icon}
                                        <span className="font-medium flex-1 text-left">{item.label}</span>
                                        <BsChevronDown
                                            size={11}
                                            className={`transition-transform duration-200 ${communicationOpen ? "rotate-180" : ""}`}
                                        />
                                    </button>
                                    <AnimatePresence initial={false}>
                                        {communicationOpen && (
                                            <motion.ul
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="overflow-hidden flex flex-col gap-1 mt-1 pl-4"
                                            >
                                                {item.children.map((c) => {
                                                    const active = isActive(item.path) && activeChannel === c.value;
                                                    return (
                                                        <li key={c.value}>
                                                            <button
                                                                type="button"
                                                                disabled={!c.enabled}
                                                                onClick={() => {
                                                                    if (!c.enabled) return;
                                                                    navigate(`${item.path}?channel=${c.value}`);
                                                                    isTab && setIsOpen(false);
                                                                }}
                                                                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[0.85rem] transition-colors ${
                                                                    active
                                                                        ? "bg-primary/80 text-white"
                                                                        : "hover:bg-white/10 hover:text-white text-white/50"
                                                                } ${!c.enabled ? "opacity-40 cursor-not-allowed hover:bg-transparent hover:text-white/50" : ""}`}
                                                            >
                                                                <c.icon size={13} />
                                                                <span>{c.label}</span>
                                                                {!c.enabled && (
                                                                    <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300">
                                                                        Soon
                                                                    </span>
                                                                )}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </motion.ul>
                                        )}
                                    </AnimatePresence>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Profile / Logout footer */}
                    <div className="px-3 pb-4 pt-3 border-t border-white/10 mt-auto">
                        <ProfileDropdown logout={handleLogout} sidebar />
                    </div>
                </div>
            </motion.div>
        </>
    );
};

export default Sidebar;
